import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { formatDateTime } from "@/lib/formatters";
import { usePermissions } from "@/hooks/use-permissions";
import { queryKeys } from "@/lib/queryKeys";
import { safetyProvider } from "@/providers/safety.provider";
import { useQuery } from "@tanstack/react-query";
import { IconAlertTriangle, IconShieldCheck } from "@tabler/icons-react";
import { formatDistanceToNow } from "date-fns";
import { Link } from "react-router";

export function ReportQueueAlert() {
  const { canRead, isLoading: loadingPermissions } = usePermissions();
  const canViewSafety = canRead("admin/safety");

  const { data: summary, isLoading } = useQuery({
    queryKey: queryKeys.safetyReportQueueSummary(),
    queryFn: () => safetyProvider.getReportQueueSummary(),
    enabled: canViewSafety,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  if (!loadingPermissions && !canViewSafety) return null;
  if (isLoading || !summary || summary.pending_count === 0) return null;

  const overdue = summary.pending_over_24h_count > 0;

  return (
    <div className="px-4 lg:px-6">
      <Link to="/dashboard/safety" className="block">
        <Card
          className={
            overdue
              ? "gap-0 overflow-hidden border-2 border-orange-500 bg-destructive/5 py-0"
              : "gap-0 overflow-hidden border-2 border-orange-500 py-0"
          }
        >
          <div
            className="h-2 w-full shrink-0"
            style={{
              backgroundImage:
                "repeating-linear-gradient(45deg, var(--color-orange-500) 0 10px, #000 10px 20px)",
            }}
          />
          <CardContent className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-center gap-3">
              {overdue ? (
                <IconAlertTriangle className="size-5 shrink-0 text-destructive" />
              ) : (
                <IconShieldCheck className="size-5 shrink-0 text-muted-foreground" />
              )}
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">
                  {summary.pending_count} pending safety{" "}
                  {summary.pending_count === 1 ? "report" : "reports"}
                </p>
                {summary.oldest_pending_at && (
                  <p className="truncate text-xs text-muted-foreground">
                    Oldest report{" "}
                    {formatDistanceToNow(new Date(summary.oldest_pending_at), {
                      addSuffix: true,
                    })}{" "}
                    ({formatDateTime(summary.oldest_pending_at)})
                  </p>
                )}
              </div>
            </div>
            {overdue && (
              <Badge variant="destructive" className="w-fit shrink-0">
                {summary.pending_over_24h_count} over 24h
              </Badge>
            )}
          </CardContent>
        </Card>
      </Link>
    </div>
  );
}
